/**
 * Form assignments panel — embedded in /forms (Assignments tab).
 */
import React, { useState, useEffect } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import AlertModal from '../components/modals/AlertModal';
import './FormsManager.css';
import {
  FORM_RELATIONSHIP_TYPES,
  FORM_RELATIONSHIP_TYPE_LABELS,
  FORM_RELATIONSHIP_TYPE_HINTS,
  formatFormRelationshipTypeLabel
} from '../constants/formRelationships';

const PROMPT_ALERT = {
  presentation: 'prompt',
  overlayOpacity: 0.16,
  overlayColor: '15, 23, 42',
  zIndex: 2000
};

export function FormAssignmentsPanel({ onSelectForm, onNotice }) {
  const [forms, setForms] = useState([]);
  const [groupsPeriods, setGroupsPeriods] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertModal, setAlertModal] = useState(null);

  const showFormAlert = (subject, body, variant = 'error', details = '') => {
    setAlertModal({
      variant,
      subject,
      body,
      details: details || undefined,
      actions: [{ label: 'OK', onClick: () => setAlertModal(null), variant: 'primary' }]
    });
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingRelId, setDeletingRelId] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addFormId, setAddFormId] = useState('');
  const [addGroupsPeriodsId, setAddGroupsPeriodsId] = useState('');
  const [addRelationshipType, setAddRelationshipType] = useState(FORM_RELATIONSHIP_TYPES.FILL_OUT);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [formsRes, gpRes, relRes] = await Promise.all([
        fetch('/api/forms'),
        fetch('/api/groups-periods'),
        fetch('/api/relationships?parent_table=groups_periods')
      ]);

      const [formsData, gpData, relData] = await Promise.all([
        formsRes.json(),
        gpRes.json(),
        relRes.json()
      ]);

      if (formsData.success) setForms(formsData.data || []);
      if (gpData.success) setGroupsPeriods(gpData.data || []);
      if (relData.success) setRelationships(relData.data || []);
    } catch (error) {
      showFormAlert('Load failed', 'Failed to load assignments', 'error', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formRelationships = relationships.filter(
    (r) => r.parent_table === 'groups_periods' && r.child_table === 'forms'
  );

  const handleAdd = async () => {
    if (!addFormId || !addGroupsPeriodsId) {
      showFormAlert('Check selection', 'Select both a form and a group-period.', 'warning');
      return;
    }

    const alreadyExists = formRelationships.some(
      (r) => r.parent_id === parseInt(addGroupsPeriodsId, 10) && r.child_id === parseInt(addFormId, 10)
    );
    if (alreadyExists) {
      showFormAlert('Already assigned', 'This form is already linked to that group-period.', 'warning');
      return;
    }

    setAdding(true);
    try {
      const response = await fetch('/api/relationships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_table: 'groups_periods',
          parent_id: parseInt(addGroupsPeriodsId, 10),
          child_table: 'forms',
          child_id: parseInt(addFormId, 10),
          relationship_type: addRelationshipType || FORM_RELATIONSHIP_TYPES.FILL_OUT
        })
      });

      const data = await response.json();
      if (data.success) {
        onNotice?.({ title: 'Assigned', text: 'Form linked to group-period.' });
        setShowAddModal(false);
        setAddFormId('');
        setAddGroupsPeriodsId('');
        setAddRelationshipType(FORM_RELATIONSHIP_TYPES.FILL_OUT);
        await loadAllData();
      } else {
        throw new Error(data.message || 'Failed to add assignment');
      }
    } catch (error) {
      showFormAlert('Add failed', 'Failed to add assignment', 'error', error.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (relId) => {
    setDeletingRelId(relId);
    try {
      const response = await fetch(`/api/relationships/${relId}`, { method: 'DELETE' });
      const data = await response.json();

      if (data.success) {
        setRemoveConfirm(null);
        onNotice?.({ title: 'Removed', text: 'Assignment removed.' });
        await loadAllData();
      } else {
        throw new Error(data.message || 'Failed to remove');
      }
    } catch (error) {
      setRemoveConfirm(null);
      showFormAlert('Remove failed', 'Failed to remove assignment', 'error', error.message);
    } finally {
      setDeletingRelId(null);
    }
  };

  const getFormTitle = (formId) => {
    const f = forms.find((x) => x.id === formId);
    return f ? f.title : `Form ${formId}`;
  };

  const getGroupsPeriodsLabel = (gpId) => {
    const gp = groupsPeriods.find((x) => x.id === gpId);
    if (!gp) return `Group-period ${gpId}`;
    const group = gp.group_name || `Group ${gp.group_id}`;
    const period = gp.period_title || `Period ${gp.period_id}`;
    const title = gp.title ? ` (${gp.title})` : '';
    return `${group} — ${period}${title}`;
  };

  return (
    <div className="forms-assignments">
      <LoadingOverlay isVisible={isLoading} message="Loading assignments…" />

      <div className="forms-assignments-toolbar">
        <p className="forms-assignments-hint">
          Link forms to group-periods so they appear for members in that context.
        </p>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="forms-btn forms-btn--primary"
        >
          + Assign
        </button>
      </div>

      {formRelationships.length === 0 && !isLoading ? (
        <div className="forms-empty forms-empty--compact">
          <p className="forms-empty-title">No assignments yet</p>
          <p className="forms-empty-text">
            Assign a form to a group-period, or assign from a form&apos;s editor on the Library tab.
          </p>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="forms-btn forms-btn--primary"
          >
            Add first assignment
          </button>
        </div>
      ) : (
        <div className="forms-table-wrap">
          <table className="forms-table">
            <thead>
              <tr>
                <th>Form</th>
                <th>Group-period</th>
                <th>Type</th>
                <th className="forms-table-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {formRelationships.map((rel) => (
                <tr key={rel.id}>
                  <td>
                    <button
                      type="button"
                      className="forms-table-link"
                      onClick={() => onSelectForm?.(rel.child_id)}
                    >
                      {getFormTitle(rel.child_id)}
                    </button>
                  </td>
                  <td>{getGroupsPeriodsLabel(rel.parent_id)}</td>
                  <td className="forms-table-muted">
                    {formatFormRelationshipTypeLabel(rel.relationship_type)}
                  </td>
                  <td className="forms-table-col-actions">
                    <button
                      type="button"
                      onClick={() => setRemoveConfirm(rel.id)}
                      disabled={deletingRelId === rel.id}
                      className="forms-btn forms-btn--danger forms-btn--compact forms-table-action"
                    >
                      {deletingRelId === rel.id ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal ? (
        <div className="forms-sheet-overlay" role="presentation" onClick={() => !adding && setShowAddModal(false)}>
          <div
            className="forms-sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="assignments-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-sheet-header">
              <h3 id="assignments-add-title" className="forms-sheet-title">Assign form</h3>
              <button
                type="button"
                className="forms-sheet-close"
                onClick={() => !adding && setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="forms-sheet-body">
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="add-form-id">Form</label>
                <select
                  id="add-form-id"
                  value={addFormId}
                  onChange={(e) => setAddFormId(e.target.value)}
                  className="forms-field-input"
                >
                  <option value="">Select a form…</option>
                  {forms.map((f) => (
                    <option key={f.id} value={f.id}>{f.title}</option>
                  ))}
                </select>
              </div>
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="add-gp-id">Group-period</label>
                <select
                  id="add-gp-id"
                  value={addGroupsPeriodsId}
                  onChange={(e) => setAddGroupsPeriodsId(e.target.value)}
                  className="forms-field-input"
                >
                  <option value="">Select a group-period…</option>
                  {groupsPeriods.map((gp) => (
                    <option key={gp.id} value={gp.id}>
                      {gp.group_name || `Group ${gp.group_id}`} — {gp.period_title || `Period ${gp.period_id}`}
                      {gp.title ? ` (${gp.title})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="add-rel-type">Behavior</label>
                <select
                  id="add-rel-type"
                  value={addRelationshipType}
                  onChange={(e) => setAddRelationshipType(e.target.value)}
                  className="forms-field-input"
                >
                  <option value={FORM_RELATIONSHIP_TYPES.FILL_OUT}>
                    {FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.FILL_OUT]}
                  </option>
                  <option value={FORM_RELATIONSHIP_TYPES.ASSIGNMENT}>
                    {FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.ASSIGNMENT]}
                  </option>
                </select>
                <p className="forms-field-hint">
                  {FORM_RELATIONSHIP_TYPE_HINTS[addRelationshipType] || ''}
                </p>
              </div>
            </div>
            <div className="forms-sheet-actions">
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--secondary"
                onClick={() => !adding && setShowAddModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--primary"
                onClick={handleAdd}
                disabled={adding || !addFormId || !addGroupsPeriodsId}
              >
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {alertModal ? (
        <AlertModal
          isOpen={true}
          onClose={() => setAlertModal(null)}
          showCloseButton={false}
          allowOverlayClose={true}
          {...PROMPT_ALERT}
          variant={alertModal.variant || 'info'}
          subject={alertModal.subject}
          body={alertModal.body}
          details={alertModal.details}
          actions={alertModal.actions}
        />
      ) : null}

      {removeConfirm ? (
        <AlertModal
          isOpen={true}
          onClose={() => !deletingRelId && setRemoveConfirm(null)}
          variant="warning"
          subject="Remove assignment?"
          body="This form will no longer be linked to that group-period."
          showCloseButton={false}
          allowOverlayClose={!deletingRelId}
          actions={[
            { label: 'Cancel', onClick: () => setRemoveConfirm(null), variant: 'secondary' },
            {
              label: deletingRelId ? 'Removing…' : 'Remove',
              onClick: () => handleRemove(removeConfirm),
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          {...PROMPT_ALERT}
        />
      ) : null}
    </div>
  );
}

export default FormAssignmentsPanel;
